import type {
  InvestmentBasicInfoApiData,
  InvestmentConsensusApiData,
  InvestmentFinancialsApiData,
  InvestmentIndicatorsApiResponse,
  InvestmentSecurityAttributesApiData,
  InvestmentTechnicalApiData,
  InvestmentTechnicalSignalsApiData,
} from '@app-types/investmentIndicatorsApi';
import type { InstrumentQueryParams } from '@utils/queryParams';
import { indicatorsClient, toApiRequestError } from './client';
import { throwDataApiError } from './dataApiError';

const isObjectPayload = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const fetchInvestmentIndicator = async <T extends object>(
  path: string,
  params: InstrumentQueryParams,
  fallbackMessage: string,
): Promise<T> => {
  try {
    const response = await indicatorsClient.get<InvestmentIndicatorsApiResponse<T>>(
      path,
      { params: { ticker: params.ticker, mkt: params.mkt } },
    );

    const payload = response.data.data;
    if (response.data.status !== 0 || !isObjectPayload(payload)) {
      throwDataApiError(response.data, response.status, fallbackMessage);
    }

    return payload as T;
  } catch (caught) {
    throw toApiRequestError(caught);
  }
};

export const fetchInvestmentBasicInfo = (
  params: InstrumentQueryParams,
): Promise<InvestmentBasicInfoApiData> =>
  fetchInvestmentIndicator('/basic-info', params, 'Basic info response is invalid.');

export const fetchInvestmentConsensus = (
  params: InstrumentQueryParams,
): Promise<InvestmentConsensusApiData> =>
  fetchInvestmentIndicator('/consensus', params, 'Consensus response is invalid.');

export const fetchInvestmentSecurityAttributes = (
  params: InstrumentQueryParams,
): Promise<InvestmentSecurityAttributesApiData> =>
  fetchInvestmentIndicator(
    '/security-attributes',
    params,
    'Security attributes response is invalid.',
  );

export const fetchInvestmentTechnical = (
  params: InstrumentQueryParams,
): Promise<InvestmentTechnicalApiData> =>
  fetchInvestmentIndicator('/technical', params, 'Technical response is invalid.');

export const fetchInvestmentFinancials = (
  params: InstrumentQueryParams,
): Promise<InvestmentFinancialsApiData> =>
  fetchInvestmentIndicator('/financials', params, 'Financials response is invalid.');

export const fetchInvestmentTechnicalSignals = (
  params: InstrumentQueryParams,
): Promise<InvestmentTechnicalSignalsApiData> =>
  fetchInvestmentIndicator(
    '/technical-signals',
    params,
    'Technical signals response is invalid.',
  );
